import React, { useMemo } from 'react'
import { DesignTemplateDetail } from '@/types/designTemplate'
import { getPlaceholders } from '../utils/normalizeTemplateData'

interface DesignSummaryProps {
  template?: DesignTemplateDetail | null
}

const inputLabels: Record<string, string> = {
  text: 'Text',
  date: 'Date',
  image: 'Image upload',
  graphic: 'Graphic',
}

/**
 * Shows what the stored design contains. Designing a template happens in the
 * Customizer, so until that editor is connected this is a read-only view of
 * what a template is carrying.
 */
const DesignSummary: React.FC<DesignSummaryProps> = ({ template }) => {
  const placeholders = useMemo(() => getPlaceholders(template?.data), [template])
  const elementCount = template?.data?.elements?.length ?? 0
  const canvas = template?.data?.canvas

  if (!template?.id) {
    return (
      <div className={'rounded-md bg-gray-50 border border-gray-200 p-md text-sm text-gray-500'}>
        Save the template first, then design it and mark which parts the customer fills in.
      </div>
    )
  }

  if (!elementCount) {
    return (
      <div className={'rounded-md bg-gray-50 border border-gray-200 p-md text-sm text-gray-500'}>
        This template has no design yet. Design it in the customizer and the artwork will appear here.
      </div>
    )
  }

  return (
    <div className={'rounded-md bg-gray-50 border border-gray-200 p-md flex flex-col gap-md'}>
      <div className={'flex flex-row gap-xl text-sm'}>
        <div>
          <p className={'text-gray-400 text-xs'}>Elements</p>
          <p className={'tabular-nums'}>{elementCount}</p>
        </div>
        <div>
          <p className={'text-gray-400 text-xs'}>Customer fills in</p>
          <p className={'tabular-nums'}>{placeholders.length}</p>
        </div>
        {canvas && (
          <div>
            <p className={'text-gray-400 text-xs'}>Designed for</p>
            <p className={'tabular-nums'}>
              {canvas.width} &times; {canvas.height} mm
            </p>
          </div>
        )}
        {template.data?.origin?.profile_type && (
          <div>
            <p className={'text-gray-400 text-xs'}>Profile</p>
            <p className={'capitalize'}>{template.data.origin.profile_type}</p>
          </div>
        )}
      </div>

      {placeholders.length > 0 && (
        <div className={'flex flex-col gap-sm'}>
          <p className={'text-gray-400 text-xs'}>What the customer is asked for</p>
          <div className={'flex flex-col gap-xs'}>
            {placeholders.map((placeholder) => (
              <div
                key={placeholder.key}
                className={
                  'flex flex-row items-center gap-sm text-sm bg-white rounded-sm px-sm py-xs border border-gray-200'
                }
              >
                <span className={'flex-1'}>{placeholder.label || placeholder.key}</span>
                <span className={'text-gray-400 text-xs'}>{inputLabels[placeholder.input] || placeholder.input}</span>
                {placeholder.required && (
                  <span className={'text-xs px-sm rounded-sm bg-primary-100 text-primary-800'}>Required</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DesignSummary
